import { useEffect, useRef, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";
import { useAuthStore, type UserRole } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <path d="M21.35 11.1H12v2.98h5.35c-.23 1.4-1.66 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.96S8.78 6.26 12 6.26c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.68 3.7 14.55 2.75 12 2.75 6.9 2.75 2.75 6.9 2.75 12S6.9 21.25 12 21.25c5.34 0 8.88-3.75 8.88-9.04 0-.6-.07-1.06-.15-1.51Z" />
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <path d="M16.37 12.6c-.02-2.26 1.85-3.35 1.93-3.4-1.05-1.54-2.69-1.75-3.27-1.77-1.39-.14-2.72.82-3.42.82-.71 0-1.79-.8-2.95-.78-1.52.02-2.92.88-3.7 2.24-1.58 2.74-.4 6.79 1.13 9.01.75 1.09 1.64 2.3 2.81 2.26 1.13-.05 1.55-.73 2.92-.73 1.36 0 1.75.73 2.94.71 1.22-.02 1.99-1.1 2.73-2.2.86-1.26 1.21-2.49 1.23-2.55-.03-.01-2.36-.9-2.38-3.6ZM14.13 5.97c.62-.76 1.04-1.8.93-2.85-.9.04-1.99.6-2.63 1.35-.58.66-1.09 1.73-.95 2.75 1 .08 2.03-.51 2.65-1.25Z" />
    </svg>
  );
}

/** Public sign-in settings, as served by GET /settings/social-auth. */
export interface SocialAuthConfig {
  google_client_id: string;
  apple_client_id: string;
  apple_redirect_uri: string;
}

const GOOGLE_SCRIPT = "https://accounts.google.com/gsi/client";
const APPLE_SCRIPT =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const scriptLoads = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const cached = scriptLoads.get(src);
  if (cached) return cached;
  const promise = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      scriptLoads.delete(src);
      el.remove();
      reject(new Error(`Could not load ${src}`));
    };
    document.head.appendChild(el);
  });
  scriptLoads.set(src, promise);
  return promise;
}

/** Where Apple sends people back to; it must match a return URL registered with Apple. */
export function defaultAppleRedirectUri() {
  return typeof window === "undefined" ? "" : `${window.location.origin}/auth/login`;
}

function destinationFor(role: UserRole | "2fa_required") {
  if (role === "2fa_required") return "/auth/login-2fa";
  if (role === "unassigned") return "/onboarding";
  if (role === "student") return "/student/dashboard";
  if (role === "partner") return "/partner/dashboard";
  return "/admin/dashboard";
}

function useSocialAuthConfig() {
  return useQuery({
    queryKey: ["social-auth-config"],
    queryFn: async () => {
      const res = await apiClient.get("/settings/social-auth", {
        skipGlobalErrorHandler: true,
      } as any);
      return res.data?.data as SocialAuthConfig;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

function UnavailableButton({ label, Icon }: { label: string; Icon: () => ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Button type="button" variant="outline" className="w-full" disabled>
            <Icon />
            Continue with {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label} sign-in isn't set up yet</TooltipContent>
    </Tooltip>
  );
}

/**
 * Google sign-in. Google only hands out an ID token through its own button, so
 * that button is shown as-is. It is drawn at a fixed pixel width, so it is
 * redrawn whenever its slot changes size; otherwise a button drawn while the
 * page was still laying out could cover only part of the slot.
 */
function GoogleButton({
  clientId,
  onToken,
  busy,
}: {
  clientId: string;
  onToken: (idToken: string) => void;
  busy: boolean;
}) {
  const slot = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let observer: ResizeObserver | undefined;
    let lastWidth = 0;

    const draw = () => {
      const google = (window as any).google;
      const el = slot.current;
      if (cancelled || !el || !google?.accounts?.id) return;
      const width = Math.round(el.getBoundingClientRect().width);
      // Wait for real layout; Google's button accepts 200 to 400 pixels.
      if (width < 200 || Math.abs(width - lastWidth) < 2) return;
      lastWidth = width;
      el.innerHTML = "";
      google.accounts.id.renderButton(el, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "pill",
        text: "continue_with",
        logo_alignment: "center",
        width: Math.min(400, width),
      });
      setReady(true);
    };

    loadScript(GOOGLE_SCRIPT)
      .then(() => {
        const google = (window as any).google;
        if (cancelled || !slot.current || !google?.accounts?.id) return;
        google.accounts.id.initialize({
          client_id: clientId,
          ux_mode: "popup",
          callback: (res: { credential?: string }) => {
            if (res.credential) onTokenRef.current(res.credential);
          },
        });
        draw();
        observer = new ResizeObserver(() => draw());
        observer.observe(slot.current);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [clientId]);

  if (failed) return <UnavailableButton label="Google" Icon={GoogleGlyph} />;

  return (
    <div className="relative flex h-10 w-full justify-center">
      <div
        ref={slot}
        className={cn("flex h-10 w-full justify-center", (!ready || busy) && "invisible")}
      />
      {(!ready || busy) && (
        <Button
          type="button"
          variant="outline"
          className="absolute inset-0 w-full"
          loading
          disabled
        >
          {busy ? "Signing in…" : "Continue with Google"}
        </Button>
      )}
    </div>
  );
}

function AppleButton({
  clientId,
  redirectUri,
  onToken,
  busy,
}: {
  clientId: string;
  redirectUri: string;
  onToken: (idToken: string, name?: string) => void;
  busy: boolean;
}) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadScript(APPLE_SCRIPT)
      .then(() => {
        const AppleID = (window as any).AppleID;
        if (cancelled || !AppleID?.auth) return;
        AppleID.auth.init({
          clientId,
          scope: "name email",
          redirectURI: redirectUri,
          usePopup: true,
        });
        setReady(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [clientId, redirectUri]);

  if (failed) return <UnavailableButton label="Apple" Icon={AppleGlyph} />;

  const handleClick = async () => {
    setOpening(true);
    try {
      const res = await (window as any).AppleID.auth.signIn();
      const idToken: string | undefined = res?.authorization?.id_token;
      if (!idToken) throw new Error("Apple returned no identity token");
      // Apple shares the person's name only on their very first sign-in.
      const first = res?.user?.name?.firstName ?? "";
      const last = res?.user?.name?.lastName ?? "";
      onToken(idToken, `${first} ${last}`.trim() || undefined);
    } catch (err: any) {
      // Closing the popup is not an error worth reporting.
      if (err?.error !== "popup_closed_by_user" && err?.error !== "user_cancelled_authorize") {
        toast.error("Apple sign-in didn't finish", {
          description: "Please try again, or sign in with email.",
        });
      }
    } finally {
      setOpening(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={handleClick}
      loading={busy || opening || !ready}
    >
      {!(busy || opening) && ready && <AppleGlyph />}
      Continue with Apple
    </Button>
  );
}

/**
 * Google / Apple sign-in. Each provider switches on once an admin saves its
 * client ID under Settings → Sign-in providers (or the server's environment
 * sets it); until then its button stays disabled.
 */
export function SocialAuthButtons() {
  const { data: config } = useSocialAuthConfig();
  const socialLogin = useAuthStore((s) => s.socialLogin);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"google" | "apple" | null>(null);

  const finish = async (provider: "google" | "apple", idToken: string, name?: string) => {
    setBusy(provider);
    try {
      const role = await socialLogin(provider, idToken, name);
      navigate({ to: destinationFor(role) as any });
    } catch (err) {
      // The API client already shows the server's reason.
      console.error(`${provider} sign-in failed:`, err);
    } finally {
      setBusy(null);
    }
  };

  const googleId = config?.google_client_id?.trim();
  const appleId = config?.apple_client_id?.trim();

  return (
    <div className="grid grid-cols-1 gap-2">
      {googleId ? (
        <GoogleButton
          clientId={googleId}
          busy={busy === "google"}
          onToken={(token) => finish("google", token)}
        />
      ) : (
        <UnavailableButton label="Google" Icon={GoogleGlyph} />
      )}
      {appleId ? (
        <AppleButton
          clientId={appleId}
          redirectUri={config?.apple_redirect_uri?.trim() || defaultAppleRedirectUri()}
          busy={busy === "apple"}
          onToken={(token, name) => finish("apple", token, name)}
        />
      ) : (
        <UnavailableButton label="Apple" Icon={AppleGlyph} />
      )}
    </div>
  );
}
